import {
  storeTokenWithExpiry,
  VERIFIED_TOKEN_EXPIRY_SECONDS,
} from "~/utils/token";

const TOKEN_STORAGE_KEY = "x_pebble_demo_token";
const EMAIL_STORAGE_KEY = "x_pebble_demo_email";
const TOKEN_EXPIRY_KEY = "x_pebble_demo_token_expiry";
const VERIFICATION_STATUS_KEY = "x_pebble_demo_verification";
const SESSION_ID_KEY = "x_pebble_demo_session";

interface ApiConfig {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
  jwtToken?: string; // Optional JWT token for authenticated requests
  guestToken?: string; // Optional guest/marketing token (uses X-Pebble-Authorization header)
}

interface ApiResponse<T = Record<string, unknown>> {
  data?: T;
  error?: string;
  status: number;
  headers?: Headers;
  traceId?: string;
}

class ApiClient {
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  private async refreshToken(): Promise<string> {
    const email = sessionStorage.getItem(EMAIL_STORAGE_KEY);
    const verificationStatus = sessionStorage.getItem(VERIFICATION_STATUS_KEY);

    if (!email || verificationStatus !== "verified") {
      throw new Error("Cannot refresh token: email not verified");
    }

    const sessionId = this.getSessionId();
    const response = await fetch("/demo/refresh-token/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, session_id: sessionId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh token: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }

    // Update stored token in sessionStorage
    storeTokenWithExpiry(
      data.token,
      data.expires_in,
      VERIFIED_TOKEN_EXPIRY_SECONDS,
      TOKEN_STORAGE_KEY,
      TOKEN_EXPIRY_KEY,
    );
    return data.token;
  }

  private async refreshJwtToken(): Promise<string> {
    // Call server-side JWT refresh endpoint
    // This endpoint will use the refresh token from the session cookie
    const response = await fetch("/auth/refresh", {
      method: "POST",
      credentials: "include", // Send cookies
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh JWT token: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }

    // Return the new access token
    return data.access_token || data.accessToken;
  }

  private async makeRequest(
    url: string,
    method: string,
    requestHeaders: Record<string, string>,
    body?: string,
    signal?: AbortSignal,
  ): Promise<Response> {
    return fetch(url, {
      method,
      headers: requestHeaders,
      body,
      signal,
      credentials: "include", // Include cookies for session-based auth
    });
  }

  async request<T = Record<string, unknown>>(
    config: ApiConfig,
  ): Promise<ApiResponse<T>> {
    const {
      method,
      url,
      headers = {},
      body,
      signal,
      jwtToken,
      guestToken,
    } = config;

    // Setup headers
    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Forwarded-Host": "pebble.finance",
      ...headers,
    };

    // Handle authentication tokens
    let validToken: string | null = null;

    // Prefer explicit guest token if provided (for marketing/embed use)
    if (guestToken) {
      requestHeaders["X-Pebble-Authorization"] = `Bearer ${guestToken}`;
      validToken = guestToken;
    }
    // Then JWT token if provided (for authenticated playground users)
    else if (jwtToken) {
      requestHeaders["Authorization"] = `JWT ${jwtToken}`;
      validToken = jwtToken;
    }
    // Fall back to demo token from sessionStorage (for guest/demo users)
    else {
      const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      const tokenExpiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);

      if (token && tokenExpiry) {
        const expiryTime = parseInt(tokenExpiry, 10);
        if (Date.now() < expiryTime) {
          requestHeaders["X-Pebble-Authorization"] = `Bearer ${token}`;
          validToken = token;
        } else {
          // Token expired, clear it
          sessionStorage.removeItem(TOKEN_STORAGE_KEY);
          sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        }
      }
    }

    const bodyString = body ? JSON.stringify(body) : undefined;

    // Make the initial request (using proxy routes)
    let response = await this.makeRequest(
      url,
      method,
      requestHeaders,
      bodyString,
      signal,
    );

    // Handle 401/403 errors with token refresh
    if ((response.status === 401 && validToken) || response.status === 403) {
      // For JWT tokens (playground/authenticated users)
      if (jwtToken) {
        try {
          console.log("JWT token expired, attempting to refresh...");
          const newToken = await this.refreshJwtToken();

          // Retry with new token
          requestHeaders["Authorization"] = `JWT ${newToken}`;
          response = await this.makeRequest(
            url,
            method,
            requestHeaders,
            bodyString,
            signal,
          );
        } catch (refreshError) {
          console.error("JWT token refresh failed:", refreshError);
          // If refresh fails, redirect to login or return error
          // For now, just return the 401 error
          return {
            error: "Authentication expired. Please log in again.",
            status: 401,
          };
        }
      }

      // For demo tokens (guest users)
      const email = sessionStorage.getItem(EMAIL_STORAGE_KEY);
      const verificationStatus = sessionStorage.getItem(
        VERIFICATION_STATUS_KEY,
      );

      if (email && verificationStatus === "verified") {
        try {
          const newToken = await this.refreshToken();

          // Retry with new token
          requestHeaders["X-Pebble-Authorization"] = `Bearer ${newToken}`;
          response = await this.makeRequest(
            url,
            method,
            requestHeaders,
            bodyString,
            signal,
          );
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          // Continue with original response if refresh fails
        }
      } else {
        console.warn(
          "Cannot refresh token: email not verified. User needs to verify email.",
        );
        // Clear the invalid token
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
      }
    }

    // If we get a 504, retry once after a delay
    if (response.status === 504) {
      // Check if request was aborted before retrying
      if (signal?.aborted) {
        return {
          error: "Request was cancelled",
          status: 0,
        };
      }

      console.log("Received 504 error, retrying once after delay...");
      try {
        // Wait 1 second before retrying
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Check again if request was aborted during the delay
        if (signal?.aborted) {
          return {
            error: "Request was cancelled",
            status: 0,
          };
        }

        response = await this.makeRequest(
          url,
          method,
          requestHeaders,
          bodyString,
          signal,
        );

        // If we still get a 504 after retry, return user-friendly message
        if (response.status === 504) {
          return {
            error:
              "The request is taking longer than expected. Please try again later.",
            status: 504,
          };
        }
      } catch (retryError) {
        // Handle abort errors gracefully
        if (
          retryError instanceof Error &&
          (retryError.name === "AbortError" || signal?.aborted)
        ) {
          return {
            error: "Request was cancelled",
            status: 0,
          };
        }

        console.error("Retry after 504 failed:", retryError);
        return {
          error:
            "The request is taking longer than expected. Please try again later.",
          status: 504,
        };
      }
    }

    const status = response.status;

    if (!response.ok) {
      let errorMessage = `API Error: ${response.statusText}`;

      try {
        const errorData = await response.json();
        errorMessage =
          errorData?.error?.message ||
          (typeof errorData?.error === "string" ? errorData.error : null) ||
          errorData?.message ||
          errorMessage;
      } catch {
        // Use default error message
      }

      return { error: errorMessage, status };
    }

    try {
      const data = await response.json();
      const traceId = response.headers?.get?.("trace_id") || undefined;
      return {
        data,
        status,
        headers: response.headers,
        traceId,
      };
    } catch {
      return {
        error: "Failed to parse response",
        status,
      };
    }
  }

  // Convenience method for the explain API (uses proxy route)
  async explainSecurity(params: {
    figi: string;
    rewriteStyle: "bulletize" | "standard";
    timePeriod: string;
    providers?: string[];
    signal?: AbortSignal;
    jwtToken?: string;
    guestToken?: string;
    url?: string;
  }): Promise<ApiResponse<{ results: { [key: string]: string } }>> {
    // Ask the explain backend to encode a per-citation text-fragment hash
    // into each source URL. NewsPopover reads the fragment off the URL
    // (preserved by the markdown link, not by `location.hash`) and passes it
    // to PebbleHighlightedArticle for inline highlighting; the external
    // "Pebble News Viewer" navigation triggers the browser-native highlight
    // off the same URL.
    const sourceUrlFormat =
      "https://{{domain}}/api/v1/news/article/{{uuid}}#:~:text={{text_highlight}}";

    return this.request({
      method: "POST",
      url: params.url || "/api/v1/explain",
      signal: params.signal,
      jwtToken: params.jwtToken,
      guestToken: params.guestToken,
      body: {
        entity: {
          type: "security",
          identifier: params.figi,
        },
        period: {
          timeframe:
            // If already in API format (AUTO, 1W, 1M, 6M), pass through
            ["AUTO", "1W", "1M", "6M"].includes(params.timePeriod)
              ? params.timePeriod
              : // Otherwise, map from UI format
                params.timePeriod === "Auto"
                ? "AUTO"
                : params.timePeriod === "1 Month"
                  ? "1M"
                  : params.timePeriod === "6 Months"
                    ? "6M"
                    : params.timePeriod,
        },
        rewrite_style: params.rewriteStyle,
        ...(params.providers && { providers: params.providers }),
        ...(sourceUrlFormat && { source_url_format: sourceUrlFormat }),
      },
    });
  }

  // Portfolio explain method - uses vite proxy to hit backend directly
  async explainPortfolio(params: {
    identifiers: { [figi: string]: number };
    timeframe: string;
    rewriteStyle?: "bulletize" | "standard";
    sectorStyle?: string;
    macroStyle?: string;
    numSecurities?: number;
    providers?: string[];
    signal?: AbortSignal;
    jwtToken?: string;
    guestToken?: string;
    url?: string;
    portfolioEntries?: Array<{
      figi: string;
      weight: number;
      value?: number;
      market_value?: number;
    }>;
    portfolioName?: string;
    v1?: boolean;
  }): Promise<
    ApiResponse<{
      explanations: {
        security: {
          positive_explanation: string;
          negative_explanation: string;
          portfolio_contribution_overview?: string;
        };
        sector: {
          positive_explanation: string;
          negative_explanation: string;
          portfolio_contribution_overview?: string;
        };
        macro: {
          positive_explanation: string;
          negative_explanation: string;
          portfolio_contribution_overview?: string;
        };
      };
      components: {
        sectors: {
          [sector: string]: {
            contribution_to_return: number;
            weight: number;
          };
        };
        securities: {
          [figi: string]: {
            contribution_to_return: number;
            weight: number;
          };
        };
      };
      figi_to_symbol_name: Record<string, string>;
    }>
  > {
    // Ask the explain backend to encode a per-citation text-fragment hash
    // into each source URL. NewsPopover reads the fragment off the URL
    // (preserved by the markdown link, not by `location.hash`) and passes it
    // to PebbleHighlightedArticle for inline highlighting; the external
    // "Pebble News Viewer" navigation triggers the browser-native highlight
    // off the same URL.
    const sourceUrlFormat =
      "https://{{domain}}/api/v1/news/article/{{uuid}}#:~:text={{text_highlight}}";

    // Note: /api routes are proxied directly to backend by vite config

    // real good check
    const isAssetmarkDemo = params.url?.includes("/assetmark");

    // // Use mock data for local development
    // if (isAssetmarkDemo && import.meta.env.DEV) {
    //   const { assetmarkDemoMockData } = await import("~/mocks/assetmark-demo");
    //   return {
    //     // eslint-disable-next-line
    //     data: assetmarkDemoMockData as any,
    //     error: null,
    //   };
    // }

    // Transform payload for AssetMark demo endpoint
    const body = isAssetmarkDemo
      ? {
          timeframe: params.timeframe,
          identifiers: params.portfolioEntries
            ? Object.fromEntries(
                params.portfolioEntries.map((entry) => [
                  entry.figi,
                  {
                    weight: entry.weight / 100,
                    market_value: entry.market_value || entry.value || 0,
                  },
                ]),
              )
            : Object.fromEntries(
                Object.entries(params.identifiers).map(([figi, weight]) => [
                  figi,
                  { weight, market_value: weight * 1000000 },
                ]),
              ),
          portfolio_name: params.portfolioName,
          num_asset_classes: 2,
          v1: params.v1 ?? false,
        }
      : {
          identifiers: params.identifiers,
          timeframe: params.timeframe, // Don't uppercase - backend expects "1W", "1M", etc
          rewrite_style: params.rewriteStyle,
          sector_style: params.sectorStyle,
          macro_style: params.macroStyle,
          num_securities: params.numSecurities || 10,
        };

    return this.request({
      method: "POST",
      url: params.url || "/api/v1/explain/portfolio",
      signal: params.signal,
      jwtToken: params.jwtToken,
      guestToken: params.guestToken,
      body: {
        ...body,
        ...(params.providers && { providers: params.providers }),
        ...(sourceUrlFormat && { source_url_format: sourceUrlFormat }),
      },
    });
  }

  // Portfolio sector visualization method - uses vite proxy to hit backend directly
  async visualizePortfolioSectorReturn(params: {
    identifiers: { [figi: string]: number };
    timeframe: string;
    dataOnly?: boolean;
    dimensions?: { width: number; height: number };
    format?: string;
    signal?: AbortSignal;
  }): Promise<
    ApiResponse<{
      sectors: string[];
      portfolio_returns: number[];
      sector_returns: number[];
      data: Array<{
        sector: string;
        portfolio_return: number;
        sector_return: number;
      }>;
      metadata: {
        portfolio_label: string;
        start_date: string;
        end_date: string;
        timeframe: string;
      };
      chart_config: {
        yaxis_range: [number, number];
        yaxis_title: string;
        bar_colors: {
          portfolio: string;
          sector: string;
        };
      };
    }>
  > {
    return this.request({
      method: "POST",
      url: "/api/v1/visualizations/portfolio_sector_return",
      signal: params.signal,
      body: {
        identifiers: params.identifiers,
        timeframe: params.timeframe,
        data_only: params.dataOnly || true,
        dimensions: params.dimensions || { width: 1044, height: 271 },
        format: params.format || "JSON",
      },
    });
  }

  async explainFund(params: {
    figi: string;
    startDate?: string;
    endDate?: string;
    rewriteStyle?: "bulletize" | "standard";
    providers?: string[];
    signal?: AbortSignal;
    jwtToken?: string;
  }): Promise<
    ApiResponse<{
      explanations: {
        security: {
          positive_explanation: string[];
          negative_explanation: string[];
          portfolio_contribution_overview?: string;
        };
        sector: {
          positive_explanation: string[];
          negative_explanation: string[];
          portfolio_contribution_overview?: string;
        };
        macro: string;
        theme_explanations?: Record<string, string>;
        key_developments?: {
          positive_explanation: string[];
          negative_explanation: string[];
          summary: string;
        };
        fund_summary?: string;
      };
      fund_summary?: string;
      price_data: {
        current_price: number;
        start_price: number;
        price_delta: number;
        percent_change: number;
        symbol: string;
        name: string;
        start_date: string;
        end_date: string;
      };
      figi_to_symbol_name?: Record<string, string>;
    }>
  > {
    // Ask the explain backend to encode a per-citation text-fragment hash
    // into each source URL. NewsPopover reads the fragment off the URL
    // (preserved by the markdown link, not by `location.hash`) and passes it
    // to PebbleHighlightedArticle for inline highlighting; the external
    // "Pebble News Viewer" navigation triggers the browser-native highlight
    // off the same URL.
    const sourceUrlFormat =
      "https://{{domain}}/api/v1/news/article/{{uuid}}#:~:text={{text_highlight}}";

    return this.request({
      method: "POST",
      url: "/api/v1/explain/fund",
      signal: params.signal,
      jwtToken: params.jwtToken,
      body: {
        entity: {
          type: "security",
          identifier: params.figi,
        },
        period: {
          timeframe: "1W",
        },
        providers: params.providers || ["mtnews"],
        rewrite_style: "bulletize",
        macro_style: "standard",
        sector_style: "standard",
        ...(sourceUrlFormat && { source_url_format: sourceUrlFormat }),
      },
    });
  }
  async rewrite(params: {
    content: string;
    prompt: string;
    preserveHyperlinks?: boolean;
    jwtToken?: string;
    signal?: AbortSignal;
  }): Promise<ApiResponse<{ rewritten_content: unknown }>> {
    return this.request({
      method: "POST",
      url: "/api/v1/rewriter",
      signal: params.signal,
      jwtToken: params.jwtToken,
      body: {
        content: params.content,
        prompt: params.prompt,
        ...(params.preserveHyperlinks && { preserve_hyperlinks: true }),
      },
    });
  }

  async getNewsTimeline(params: {
    ticker: string;
    startDatetime: string;
    endDatetime: string;
    providers?: string[];
    includeLowSignal?: boolean;
    signal?: AbortSignal;
    jwtToken?: string;
    guestToken?: string;
  }): Promise<
    ApiResponse<{
      security: {
        figi: string;
        ticker: string;
        region: string;
        name: string;
      };
      start_datetime: string;
      end_datetime: string;
      include_low_signal: boolean;
      total_clusters: number;
      total_articles_considered: number;
      clusters: Array<{
        cluster_id: number;
        first_surfaced_at: string;
        headline: string;
        relevance_score: number;
        article_count: number;
        providers: string[];
        articles: Array<{
          uuid: string;
          provider: string;
          datetime: string;
          headline: string;
          source: string;
          url: string;
        }>;
      }>;
    }>
  > {
    const searchParams = new URLSearchParams({
      ticker: params.ticker,
      start_datetime: params.startDatetime,
      end_datetime: params.endDatetime,
      ...(params.providers && { providers: params.providers.join(",") }),
      ...(params.includeLowSignal !== undefined && {
        include_low_signal: params.includeLowSignal.toString(),
      }),
    });

    return this.request({
      method: "GET",
      url: `/api/v1/news/timeline?${searchParams.toString()}`,
      signal: params.signal,
      jwtToken: params.jwtToken,
      guestToken: params.guestToken,
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export type { ApiResponse };
